import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const REFERRAL_COOKIE_NAME = 'ref_code';
const REFERRAL_EXPIRY_DAYS = 30; // Referral code expires after 30 days

/**
 * Set a cookie with expiry
 */
function setCookie(name: string, value: string, days: number): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

/**
 * Get a cookie value
 */
function getCookie(name: string): string | null {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }
  return null;
}

/**
 * Delete a cookie
 */
function deleteCookie(name: string): void {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

/**
 * ReferralTracker - Tracks referral codes from URL parameters
 *
 * This component checks for ?ref=CODE in the URL and stores it in a cookie.
 * The code persists for 30 days so users can register later and still be attributed
 * to the affiliate/referrer.
 */
export function ReferralTracker() {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const refCode = searchParams.get('ref');

    if (refCode) {
      // Store the referral code in cookie
      setCookie(REFERRAL_COOKIE_NAME, refCode, REFERRAL_EXPIRY_DAYS);

      // Remove the ref parameter from URL without page reload (cleaner UX)
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('ref');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [searchParams]);

  return null; // This component doesn't render anything
}

/**
 * Get the stored referral code from cookie
 */
export function getStoredReferralCode(): string | null {
  return getCookie(REFERRAL_COOKIE_NAME);
}

/**
 * Clear the stored referral code (e.g., after successful registration)
 */
export function clearStoredReferralCode(): void {
  deleteCookie(REFERRAL_COOKIE_NAME);
}

export default ReferralTracker;
